import React, { FC } from 'react';
import { FieldPricing } from '../../../../helpers/pricing';

export interface TableFieldProps {
  name: string;
  fieldPricing: FieldPricing;
}

enum TableCategories {
  MerchantDiscountRate = 'Merchant Discount Rate',
  TransactionFee = 'Transaction Fee',
  Payouts = 'Payouts',
  CreditFee = 'Credit Fee',
  ChargebackHandlingFee = 'Chargeback Handling Fee',
  RollingReserve = 'Rolling Reserve',
  NoPrice = 'no-price',
  AllCountries = 'all-countries'
}

enum TablePaymentMethods {
  P2P = 'P2P',
  B2C = 'B2C payout'
}

interface RowSpanPerKey {
  [key: string]: number;
}

interface CountryFieldRowSpan {
  [key: string]: RowSpanPerKey;
}

interface TableRowCell {
  value: string;
  rowSpan: number;
}

type TableRow = TableRowCell[];

const TableField: FC<TableFieldProps> = ({ name, fieldPricing }) => {
  let totalRows: number = 0;
  let rowSpanForCountries: RowSpanPerKey = {};
  let rowSpanForPaymentMethods: CountryFieldRowSpan = {};
  let rowSpanForPrices: CountryFieldRowSpan = {};

  let tableRows: TableRow[] = [];

  for (const price in fieldPricing) {
    if (!fieldPricing.hasOwnProperty(price)) {
      continue;
    }

    for (const country in fieldPricing[price]) {
      if (!fieldPricing[price].hasOwnProperty(country)) {
        continue;
      }

      if (rowSpanForCountries[country]) {
        rowSpanForCountries[country] += fieldPricing[price][country].length;
      } else {
        rowSpanForCountries[country] = fieldPricing[price][country].length;
      }

      if (!rowSpanForPrices[country]) {
        rowSpanForPrices[country] = {};
      }

      if (!rowSpanForPaymentMethods[country]) {
        rowSpanForPaymentMethods[country] = {};
      }

      rowSpanForPrices[country][price] = fieldPricing[price][country].length;

      for (const paymentMethod of fieldPricing[price][country]) {
        rowSpanForPaymentMethods[country][paymentMethod] = 1;
        totalRows++;
      }
    }
  }

  const rowSpanForName = totalRows;

  const countriesKeys = Object.keys(rowSpanForCountries);
  let countriesNextKey = 0;
  let countriesDelay = 0;

  let paymentMethodsNextKey = 0;
  let paymentMethodsDelay = 0;

  let pricesNextKey = 0;
  let pricesDelay = 0;

  for (let i = 0; i < totalRows; i++) {
    const row: { value: string; rowSpan: number }[] = [];

    if (i === 0) {
      row.push({
        value: name,
        rowSpan: rowSpanForName
      });
    }

    if (countriesDelay === i) {
      const countryKey = countriesKeys[countriesNextKey];
      const rowSpan = rowSpanForCountries[countryKey];

      row.push({
        value: countryKey,
        rowSpan
      });

      countriesNextKey++;
      countriesDelay = i + rowSpan;

      paymentMethodsNextKey = 0;
      paymentMethodsDelay = i;
      pricesNextKey = 0;
      pricesDelay = i;
    }

    if (paymentMethodsDelay === i) {
      const countryKey = countriesKeys[countriesNextKey - 1];
      const paymentMethodsKeys = Object.keys(rowSpanForPaymentMethods[countryKey]);
      const paymentMethodKey = paymentMethodsKeys[paymentMethodsNextKey];
      const rowSpan = rowSpanForPaymentMethods[countryKey][paymentMethodKey];

      row.push({
        value: paymentMethodKey,
        rowSpan
      });

      paymentMethodsNextKey++;
      paymentMethodsDelay = i + rowSpan;
    }

    if (pricesDelay === i) {
      const countryKey = countriesKeys[countriesNextKey - 1];
      const pricesKeys = Object.keys(rowSpanForPrices[countryKey]);
      const priceKey = pricesKeys[pricesNextKey];
      const rowSpan = rowSpanForPrices[countryKey][priceKey];

      row.push({
        value: priceKey,
        rowSpan
      });

      pricesNextKey++;
      pricesDelay = i + rowSpan;
    }

    tableRows.push(row);
  }

  const getTableDataClassName = (colValue: string): string => {
    if (colValue === TableCategories.MerchantDiscountRate
      || colValue === TableCategories.TransactionFee
      || colValue === TableCategories.Payouts
      || colValue === TableCategories.CreditFee
      || colValue === TableCategories.ChargebackHandlingFee
      || colValue === TableCategories.RollingReserve) {
      return 'table-title-box';
    }

    if (colValue === TablePaymentMethods.P2P|| colValue === TablePaymentMethods.B2C) {
      return 'table-data-box';
    }

    if (colValue === TableCategories.NoPrice || /\d/.test(colValue)) {
      return 'table-price-box';
    }

    return 'table-data-box';
  };

  const getTableDataSpanClassName = (colValue: string): string => {
    if (colValue === TableCategories.MerchantDiscountRate
      || colValue === TableCategories.TransactionFee
      || colValue === TableCategories.Payouts
      || colValue === TableCategories.CreditFee
      || colValue === TableCategories.ChargebackHandlingFee
      || colValue === TableCategories.RollingReserve) {
      return 'table-title';
    }

    if (colValue === TablePaymentMethods.P2P|| colValue === TablePaymentMethods.B2C) {
      return 'table-data';
    }

    if (colValue === TableCategories.NoPrice || /\d/.test(colValue)) {
      return 'table-price';
    }

    return 'table-data';
  };

  return (
    <>
      {tableRows.map((columns, index) => (
        <tr key={`table-row-${name}-${index}`}>
          {columns
            .filter((col) => col.value !== TableCategories.AllCountries)
            .map((col) => (
              <td
                key={`table-column-${name}-${col.value}`}
                rowSpan={col.rowSpan}
                className={getTableDataClassName(col.value)}
              >
                <span className={getTableDataSpanClassName(col.value)}>
                  {col.value === 'no-price' ? '-' : col.value}
                </span>
              </td>
            ))}
        </tr>
      ))}
    </>
  );
};

export default TableField;